/**
 * Owner Dashboard — pump-owner analytics.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import LoginHeaderOperational from "../LoginHeaderOperational";
import Sidebar_Progress from "../../layouts/Operational_Sidebar_Progress.component";
import Loader from "../Employee/Loader.component";
import OwnerSalesTrendChart from "./OwnerSalesTrendChart";
import OwnerProfitMarginChart from "./OwnerProfitMarginChart";
import OwnerFuelMixChart from "./OwnerFuelMixChart";
import OwnerExpenseBreakdownChart from "./OwnerExpenseBreakdownChart";
import OwnerEmployeePerformanceChart from "./OwnerEmployeePerformanceChart";
import OwnerPurchaseInsightsChart from "./OwnerPurchaseInsightsChart";
import OwnerCustomerInsightsChart from "./OwnerCustomerInsightsChart";
import OwnerNozzleAnalyticsChart from "./OwnerNozzleAnalyticsChart";
import OwnerStockInsightsChart from "./OwnerStockInsightsChart";
import { API_URL } from "../../../constant/API_Settings";
import { clearAuthStorage, getValidAuthToken } from "../../../utils/authToken";
import {
  fetchAdminPumpAnalyticsDateDefaults,
  fetchAdminPumpOwnerDashboard,
} from "../Admin/adminApi";

const OwnerDashboard = ({ embedded = false, pumpId = null, defaultFromDate = '', defaultToDate = '', syncDateRange = null, dateRangeKey = 0, hideDateFilter = false, ...props }) => {
  const history = useHistory();
  const authFailureHandledRef = useRef(false);
  const [loading, setLoading] = useState(!embedded);
  const [embeddedLoadRequested, setEmbeddedLoadRequested] = useState(false);
  const [access, setAccess] = useState({ allowed: false, message: "" });
  const [minDate, setMinDate] = useState("");
  const [draftFromDate, setDraftFromDate] = useState("");
  const [draftToDate, setDraftToDate] = useState("");
  const [appliedDateRange, setAppliedDateRange] = useState({ from_date: "", to_date: "" });
  const [salesTrendData, setSalesTrendData] = useState([]);
  const [salesTrendSeries, setSalesTrendSeries] = useState([]);
  const [salesTrendLoading, setSalesTrendLoading] = useState(false);
  const [profitMarginData, setProfitMarginData] = useState([]);
  const [profitMarginSeries, setProfitMarginSeries] = useState([]);
  const [profitMarginLoading, setProfitMarginLoading] = useState(false);
  const [fuelMixData, setFuelMixData] = useState([]);
  const [fuelMixSeries, setFuelMixSeries] = useState([]);
  const [fuelMixSummary, setFuelMixSummary] = useState({});
  const [fuelMixLoading, setFuelMixLoading] = useState(false);
  const [expenseData, setExpenseData] = useState([]);
  const [expenseSeries, setExpenseSeries] = useState([]);
  const [expenseSummary, setExpenseSummary] = useState({});
  const [expenseLoading, setExpenseLoading] = useState(false);
  const [employeeTopSales, setEmployeeTopSales] = useState([]);
  const [employeeTopProductivity, setEmployeeTopProductivity] = useState([]);
  const [employeeRows, setEmployeeRows] = useState([]);
  const [employeeSummary, setEmployeeSummary] = useState({});
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [purchaseData, setPurchaseData] = useState([]);
  const [purchaseSeries, setPurchaseSeries] = useState([]);
  const [purchasePriceData, setPurchasePriceData] = useState([]);
  const [purchasePriceSeries, setPurchasePriceSeries] = useState([]);
  const [purchaseSummary, setPurchaseSummary] = useState({});
  const [purchaseBySupplier, setPurchaseBySupplier] = useState([]);
  const [purchaseByProduct, setPurchaseByProduct] = useState([]);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [customerData, setCustomerData] = useState([]);
  const [customerSeries, setCustomerSeries] = useState([]);
  const [customerSummary, setCustomerSummary] = useState({});
  const [customerTopSales, setCustomerTopSales] = useState([]);
  const [customerTopOutstanding, setCustomerTopOutstanding] = useState([]);
  const [customerByChannel, setCustomerByChannel] = useState([]);
  const [customerOverLimit, setCustomerOverLimit] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [nozzleData, setNozzleData] = useState([]);
  const [nozzleSeries, setNozzleSeries] = useState([]);
  const [nozzleSummary, setNozzleSummary] = useState({});
  const [nozzleTopSales, setNozzleTopSales] = useState([]);
  const [nozzleTopLitres, setNozzleTopLitres] = useState([]);
  const [nozzleByShare, setNozzleByShare] = useState([]);
  const [nozzleRows, setNozzleRows] = useState([]);
  const [nozzleLoading, setNozzleLoading] = useState(false);
  const [stockData, setStockData] = useState([]);
  const [stockSeries, setStockSeries] = useState([]);
  const [stockValueData, setStockValueData] = useState([]);
  const [stockValueSeries, setStockValueSeries] = useState([]);
  const [stockSummary, setStockSummary] = useState({});
  const [stockByCategory, setStockByCategory] = useState([]);
  const [stockFuelByProduct, setStockFuelByProduct] = useState([]);
  const [stockTanks, setStockTanks] = useState([]);
  const [stockTopNf, setStockTopNf] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);

  const redirectToLogin = useCallback(() => {
    if (authFailureHandledRef.current) {
      return;
    }
    authFailureHandledRef.current = true;
    clearAuthStorage();
    history.push("/login");
  }, [history]);

  const fetchOwnerDashboard = useCallback(
    (endpoint, dateRange, handlers) => {
      if (!dateRange?.from_date || !dateRange?.to_date) {
        return;
      }

      handlers.setLoading(true);

      if (embedded && pumpId) {
        fetchAdminPumpOwnerDashboard(pumpId, endpoint, dateRange.from_date, dateRange.to_date)
          .then((responseData) => handlers.onSuccess(responseData))
          .catch(() => handlers.onError())
          .finally(() => handlers.setLoading(false));
        return;
      }

      const token = getValidAuthToken();
      if (!token) {
        redirectToLogin();
        return;
      }

      fetch(`${API_URL}/owner-dashboard/${endpoint}`, {
        method: "post",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: JSON.stringify({
          from_date: dateRange.from_date,
          to_date: dateRange.to_date,
        }),
      })
        .then(async (response) => {
          if (response.status === 401 || response.status === 403) {
            if (response.status === 401) {
              redirectToLogin();
            }
            throw new Error("ACCESS_DENIED");
          }
          return response.json();
        })
        .then((responseData) => {
          handlers.onSuccess(responseData);
        })
        .catch(() => {
          handlers.onError();
        })
        .finally(() => handlers.setLoading(false));
    },
    [embedded, pumpId, redirectToLogin]
  );

  const fetchSalesTrend = useCallback(
    (dateRange) => {
      fetchOwnerDashboard("sales-trend", dateRange, {
        setLoading: setSalesTrendLoading,
        onSuccess: (responseData) => {
          setSalesTrendData(responseData.data || []);
          setSalesTrendSeries(responseData.series || []);
        },
        onError: () => {
          setSalesTrendData([]);
          setSalesTrendSeries([]);
        },
      });
    },
    [fetchOwnerDashboard]
  );

  const fetchProfitMarginTrend = useCallback(
    (dateRange) => {
      fetchOwnerDashboard("profit-margin-trend", dateRange, {
        setLoading: setProfitMarginLoading,
        onSuccess: (responseData) => {
          setProfitMarginData(responseData.data || []);
          setProfitMarginSeries(responseData.series || []);
        },
        onError: () => {
          setProfitMarginData([]);
          setProfitMarginSeries([]);
        },
      });
    },
    [fetchOwnerDashboard]
  );

  const fetchFuelMix = useCallback(
    (dateRange) => {
      fetchOwnerDashboard("fuel-mix", dateRange, {
        setLoading: setFuelMixLoading,
        onSuccess: (responseData) => {
          setFuelMixData(responseData.data || []);
          setFuelMixSeries(responseData.series || []);
          setFuelMixSummary(responseData.summary || {});
        },
        onError: () => {
          setFuelMixData([]);
          setFuelMixSeries([]);
          setFuelMixSummary({});
        },
      });
    },
    [fetchOwnerDashboard]
  );

  const fetchExpenseBreakdown = useCallback(
    (dateRange) => {
      fetchOwnerDashboard("expense-breakdown", dateRange, {
        setLoading: setExpenseLoading,
        onSuccess: (responseData) => {
          setExpenseData(responseData.data || []);
          setExpenseSeries(responseData.series || []);
          setExpenseSummary(responseData.summary || {});
        },
        onError: () => {
          setExpenseData([]);
          setExpenseSeries([]);
          setExpenseSummary({});
        },
      });
    },
    [fetchOwnerDashboard]
  );

  const fetchEmployeePerformance = useCallback(
    (dateRange) => {
      fetchOwnerDashboard("employee-performance", dateRange, {
        setLoading: setEmployeeLoading,
        onSuccess: (responseData) => {
          setEmployeeTopSales(responseData.top_by_sales || []);
          setEmployeeTopProductivity(responseData.top_by_productivity || []);
          setEmployeeRows(responseData.employees || []);
          setEmployeeSummary(responseData.summary || {});
        },
        onError: () => {
          setEmployeeTopSales([]);
          setEmployeeTopProductivity([]);
          setEmployeeRows([]);
          setEmployeeSummary({});
        },
      });
    },
    [fetchOwnerDashboard]
  );

  const fetchPurchaseInsights = useCallback(
    (dateRange) => {
      fetchOwnerDashboard("purchase-insights", dateRange, {
        setLoading: setPurchaseLoading,
        onSuccess: (responseData) => {
          setPurchaseData(responseData.data || []);
          setPurchaseSeries(responseData.series || []);
          setPurchasePriceData(responseData.price_data || []);
          setPurchasePriceSeries(responseData.price_series || []);
          setPurchaseSummary(responseData.summary || {});
          setPurchaseBySupplier(responseData.by_supplier || []);
          setPurchaseByProduct(responseData.by_product || []);
        },
        onError: () => {
          setPurchaseData([]);
          setPurchaseSeries([]);
          setPurchasePriceData([]);
          setPurchasePriceSeries([]);
          setPurchaseSummary({});
          setPurchaseBySupplier([]);
          setPurchaseByProduct([]);
        },
      });
    },
    [fetchOwnerDashboard]
  );

  const fetchCustomerInsights = useCallback(
    (dateRange) => {
      fetchOwnerDashboard("customer-insights", dateRange, {
        setLoading: setCustomerLoading,
        onSuccess: (responseData) => {
          setCustomerData(responseData.data || []);
          setCustomerSeries(responseData.series || []);
          setCustomerSummary(responseData.summary || {});
          setCustomerTopSales(responseData.top_by_sales || []);
          setCustomerTopOutstanding(responseData.top_by_outstanding || []);
          setCustomerByChannel(responseData.by_channel || []);
          setCustomerOverLimit(responseData.over_limit || []);
        },
        onError: () => {
          setCustomerData([]);
          setCustomerSeries([]);
          setCustomerSummary({});
          setCustomerTopSales([]);
          setCustomerTopOutstanding([]);
          setCustomerByChannel([]);
          setCustomerOverLimit([]);
        },
      });
    },
    [fetchOwnerDashboard]
  );

  const fetchNozzleInsights = useCallback(
    (dateRange) => {
      fetchOwnerDashboard("nozzle-insights", dateRange, {
        setLoading: setNozzleLoading,
        onSuccess: (responseData) => {
          setNozzleData(responseData.data || []);
          setNozzleSeries(responseData.series || []);
          setNozzleSummary(responseData.summary || {});
          setNozzleTopSales(responseData.top_by_sales || []);
          setNozzleTopLitres(responseData.top_by_litres || []);
          setNozzleByShare(responseData.by_share || []);
          setNozzleRows(responseData.nozzles || []);
        },
        onError: () => {
          setNozzleData([]);
          setNozzleSeries([]);
          setNozzleSummary({});
          setNozzleTopSales([]);
          setNozzleTopLitres([]);
          setNozzleByShare([]);
          setNozzleRows([]);
        },
      });
    },
    [fetchOwnerDashboard]
  );

  const fetchStockInsights = useCallback(
    (dateRange) => {
      fetchOwnerDashboard("stock-insights", dateRange, {
        setLoading: setStockLoading,
        onSuccess: (responseData) => {
          setStockData(responseData.stock_data || []);
          setStockSeries(responseData.stock_series || []);
          setStockValueData(responseData.value_data || []);
          setStockValueSeries(responseData.value_series || []);
          setStockSummary(responseData.summary || {});
          setStockByCategory(responseData.by_category || []);
          setStockFuelByProduct(responseData.fuel_by_product || []);
          setStockTanks(responseData.tanks || []);
          setStockTopNf(responseData.top_nf_products || []);
        },
        onError: () => {
          setStockData([]);
          setStockSeries([]);
          setStockValueData([]);
          setStockValueSeries([]);
          setStockSummary({});
          setStockByCategory([]);
          setStockFuelByProduct([]);
          setStockTanks([]);
          setStockTopNf([]);
        },
      });
    },
    [fetchOwnerDashboard]
  );

  const loadAllDashboardData = useCallback(
    (dateRange) => {
      fetchSalesTrend(dateRange);
      fetchProfitMarginTrend(dateRange);
      fetchFuelMix(dateRange);
      fetchStockInsights(dateRange);
      fetchExpenseBreakdown(dateRange);
      fetchEmployeePerformance(dateRange);
      fetchNozzleInsights(dateRange);
      fetchPurchaseInsights(dateRange);
      fetchCustomerInsights(dateRange);
    },
    [
      fetchSalesTrend,
      fetchProfitMarginTrend,
      fetchFuelMix,
      fetchStockInsights,
      fetchExpenseBreakdown,
      fetchEmployeePerformance,
      fetchNozzleInsights,
      fetchPurchaseInsights,
      fetchCustomerInsights,
    ]
  );

  const fetchDateDefaults = useCallback(async () => {
    if (embedded && pumpId) {
      try {
        return await fetchAdminPumpAnalyticsDateDefaults(pumpId);
      } catch {
        return null;
      }
    }

    const token = getValidAuthToken();
    if (!token) {
      redirectToLogin();
      return null;
    }

    try {
      const response = await fetch(`${API_URL}/owner-dashboard/date-defaults`, {
        method: "get",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (response.status === 401) {
        redirectToLogin();
        return null;
      }

      if (!response.ok) {
        return null;
      }

      return response.json();
    } catch (error) {
      return null;
    }
  }, [embedded, pumpId, redirectToLogin]);

  const handleApplyDateRange = () => {
    if (!draftFromDate || !draftToDate || draftFromDate > draftToDate) {
      return;
    }

    const nextRange = { from_date: draftFromDate, to_date: draftToDate };
    setAppliedDateRange(nextRange);
    loadAllDashboardData(nextRange);
  };

  const handleEmbeddedLoad = () => {
    if (!appliedDateRange.from_date || !appliedDateRange.to_date) {
      return;
    }
    setEmbeddedLoadRequested(true);
    loadAllDashboardData(appliedDateRange);
  };

  useEffect(() => {
    if (embedded && pumpId) {
      return undefined;
    }

    let cancelled = false;

    const verifyAccess = async () => {
      try {
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("AUTH_TOKEN") || ""}`,
          Accept: "application/json",
        };

        const [urlResponse, ownerResponse] = await Promise.all([
          fetch(`${API_URL}/check_url`, {
            method: "post",
            headers,
            body: JSON.stringify({ pathurl: window.location.pathname }),
          }),
          fetch(`${API_URL}/is_owner`, { method: "get", headers }),
        ]);

        if (urlResponse.status === 401 || ownerResponse.status === 401) {
          redirectToLogin();
          return;
        }

        const urlData = await urlResponse.json();
        const roleName = await ownerResponse.json();
        const isOwner = String(roleName || "").toLowerCase() === "owner";

        if (cancelled) {
          return;
        }

        if (!isOwner) {
          setAccess({
            allowed: false,
            message: "This page is only available to pump owners.",
          });
          setLoading(false);
          return;
        }

        if (urlData.allowurl !== 1) {
          setAccess({
            allowed: false,
            message: "You do not have permission to view this page.",
          });
          setLoading(false);
          return;
        }

        const defaults = await fetchDateDefaults();
        if (cancelled) {
          return;
        }

        if (defaults?.from_date && defaults?.to_date) {
          setMinDate(defaults.min_date || defaults.from_date);
          setDraftFromDate(defaults.from_date);
          setDraftToDate(defaults.to_date);
          const nextRange = {
            from_date: defaults.from_date,
            to_date: defaults.to_date,
          };
          setAppliedDateRange(nextRange);
          loadAllDashboardData(nextRange);
        }

        setAccess({ allowed: true, message: "" });
        setLoading(false);
      } catch (error) {
        if (!cancelled) {
          setAccess({
            allowed: false,
            message: "Unable to verify owner access. Please try again.",
          });
          setLoading(false);
        }
      }
    };

    verifyAccess();

    return () => {
      cancelled = true;
    };
  }, [embedded, pumpId, redirectToLogin, fetchDateDefaults, loadAllDashboardData]);

  useEffect(() => {
    if (!embedded || !pumpId) {
      return;
    }

    const from = syncDateRange?.from_date || defaultFromDate;
    const to = syncDateRange?.to_date || defaultToDate;
    if (!from || !to) {
      return;
    }

    const nextRange = { from_date: from, to_date: to };
    setMinDate(defaultFromDate || from);
    setDraftFromDate(from);
    setDraftToDate(to);
    setAppliedDateRange(nextRange);
    setAccess({ allowed: true, message: '' });
    setLoading(false);
    setEmbeddedLoadRequested(false);
  }, [
    embedded,
    pumpId,
    dateRangeKey,
    syncDateRange?.from_date,
    syncDateRange?.to_date,
    defaultFromDate,
    defaultToDate,
  ]);

  const dashboardBody = (
    <>
      {loading ? (
        <div className="form-wrap mt-3 text-center">
          <Loader />
        </div>
      ) : !access.allowed ? (
        <div className="form-wrap mt-3">
          <div className="text-center">{access.message}</div>
        </div>
      ) : (
        <div className={`row ${embedded ? 'mt-2' : 'mt-md-5 mt-3'} form-container justify-content-center`}>
                {embedded && !embeddedLoadRequested && (
                <div className="col-md-12 mb-3">
                  <div className="admin-panel text-center py-4">
                    <p className="text-muted mb-3">
                      Load owner dashboard charts for <strong>{appliedDateRange.from_date}</strong> → <strong>{appliedDateRange.to_date}</strong>.
                      This fetches sales, stock, expenses, and team metrics.
                    </p>
                    <button type="button" className="btn btn-primary" onClick={handleEmbeddedLoad}>
                      Load owner dashboard
                    </button>
                  </div>
                </div>
                )}
                {!hideDateFilter && (
                <div className="col-md-12 mb-3">
                  <div className="dash-card owner-date-filter">
                    <div className="row g-3 align-items-end">
                      <div className="col-md-4 col-lg-3">
                        <label className="form-label small text-muted mb-1" htmlFor="owner-from-date">
                          From date
                        </label>
                        <input
                          id="owner-from-date"
                          type="date"
                          className="form-control"
                          min={minDate || undefined}
                          max={draftToDate || undefined}
                          value={draftFromDate}
                          onChange={(e) => setDraftFromDate(e.target.value)}
                        />
                      </div>
                      <div className="col-md-4 col-lg-3">
                        <label className="form-label small text-muted mb-1" htmlFor="owner-to-date">
                          To date
                        </label>
                        <input
                          id="owner-to-date"
                          type="date"
                          className="form-control"
                          min={draftFromDate || minDate || undefined}
                          max={new Date().toISOString().slice(0, 10)}
                          value={draftToDate}
                          onChange={(e) => setDraftToDate(e.target.value)}
                        />
                      </div>
                      <div className="col-md-4 col-lg-3">
                        <button
                          type="button"
                          className="btn btn-primary w-100"
                          onClick={handleApplyDateRange}
                          disabled={!draftFromDate || !draftToDate || draftFromDate > draftToDate}
                        >
                          Apply
                        </button>
                      </div>
                      <div className="col-lg-3">
                        <p className="small text-muted mb-0 owner-date-filter__hint">
                          Default: current financial year opening to today.
                          {appliedDateRange.from_date && appliedDateRange.to_date
                            ? ` Showing ${appliedDateRange.from_date} to ${appliedDateRange.to_date}.`
                            : ""}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {(!embedded || embeddedLoadRequested) && (
                <>
                <div className="col-md-12 mb-3">
                  <div className="dash-card owner-chart-card">
                    <div className="mb-2">
                      <h6 className="text-primary mb-1">Sales Trend</h6>
                      <p className="small text-muted mb-0">
                        Stacked sales by product with total trend line (₹).
                      </p>
                    </div>

                    <OwnerSalesTrendChart
                      data={salesTrendData}
                      series={salesTrendSeries}
                      loading={salesTrendLoading}
                    />
                  </div>
                </div>

                <div className="col-md-12 mb-3">
                  <div className="dash-card owner-chart-card">
                    <div className="mb-2">
                      <h6 className="text-primary mb-1">Profit & Margin</h6>
                      <p className="small text-muted mb-0">
                        Gross margin by product (sales − COGS) with margin % trend.
                      </p>
                    </div>

                    <OwnerProfitMarginChart
                      data={profitMarginData}
                      series={profitMarginSeries}
                      loading={profitMarginLoading}
                    />
                  </div>
                </div>

                <div className="col-md-12 mb-3">
                  <div className="dash-card owner-chart-card">
                    <div className="mb-2">
                      <h6 className="text-primary mb-1">Fuel Mix</h6>
                      <p className="small text-muted mb-0">
                        Fuel sales share by product (% of amount and liters).
                      </p>
                    </div>

                    <OwnerFuelMixChart
                      data={fuelMixData}
                      series={fuelMixSeries}
                      summary={fuelMixSummary}
                      loading={fuelMixLoading}
                    />
                  </div>
                </div>

                <div className="col-md-12 mb-3">
                  <div className="dash-card owner-chart-card">
                    <div className="mb-2">
                      <h6 className="text-primary mb-1">Stock & Value</h6>
                      <p className="small text-muted mb-0">
                        Fuel stock litres, inventory value trend, tank fill, and product breakdown.
                      </p>
                    </div>

                    <OwnerStockInsightsChart
                      stockData={stockData}
                      stockSeries={stockSeries}
                      valueData={stockValueData}
                      valueSeries={stockValueSeries}
                      summary={stockSummary}
                      byCategory={stockByCategory}
                      fuelByProduct={stockFuelByProduct}
                      tanks={stockTanks}
                      topNfProducts={stockTopNf}
                      loading={stockLoading}
                    />
                  </div>
                </div>

                <div className="col-md-12 mb-3">
                  <div className="dash-card owner-chart-card">
                    <div className="mb-2">
                      <h6 className="text-primary mb-1">Expense Breakdown</h6>
                      <p className="small text-muted mb-0">
                        P&L expenses by category with period split donut.
                      </p>
                    </div>

                    <OwnerExpenseBreakdownChart
                      data={expenseData}
                      series={expenseSeries}
                      summary={expenseSummary}
                      loading={expenseLoading}
                    />
                  </div>
                </div>

                <div className="col-md-12 mb-3">
                  <div className="dash-card owner-chart-card">
                    <div className="mb-2">
                      <h6 className="text-primary mb-1">Employee Performance</h6>
                      <p className="small text-muted mb-0">
                        Top attendants by total sales and sales per hour worked.
                      </p>
                    </div>

                    <OwnerEmployeePerformanceChart
                      topBySales={employeeTopSales}
                      topByProductivity={employeeTopProductivity}
                      employees={employeeRows}
                      summary={employeeSummary}
                      loading={employeeLoading}
                    />
                  </div>
                </div>

                <div className="col-md-12 mb-3">
                  <div className="dash-card owner-chart-card">
                    <div className="mb-2">
                      <h6 className="text-primary mb-1">Nozzle Analytics</h6>
                      <p className="small text-muted mb-0">
                        Per-nozzle fuel sales, litres, share, and attendant usage.
                      </p>
                    </div>

                    <OwnerNozzleAnalyticsChart
                      data={nozzleData}
                      series={nozzleSeries}
                      summary={nozzleSummary}
                      topBySales={nozzleTopSales}
                      topByLitres={nozzleTopLitres}
                      byShare={nozzleByShare}
                      nozzles={nozzleRows}
                      loading={nozzleLoading}
                    />
                  </div>
                </div>

                <div className="col-md-12 mb-3">
                  <div className="dash-card owner-chart-card">
                    <div className="mb-2">
                      <h6 className="text-primary mb-1">Purchase Insights</h6>
                      <p className="small text-muted mb-0">
                        Fuel, non-fuel, and cylinder purchases with price and supplier split.
                      </p>
                    </div>

                    <OwnerPurchaseInsightsChart
                      data={purchaseData}
                      series={purchaseSeries}
                      priceData={purchasePriceData}
                      priceSeries={purchasePriceSeries}
                      summary={purchaseSummary}
                      bySupplier={purchaseBySupplier}
                      byProduct={purchaseByProduct}
                      loading={purchaseLoading}
                    />
                  </div>
                </div>

                <div className="col-md-12 mb-3">
                  <div className="dash-card owner-chart-card">
                    <div className="mb-2">
                      <h6 className="text-primary mb-1">Customer Insights</h6>
                      <p className="small text-muted mb-0">
                        Credit sales, collections, outstanding balances, and channel mix.
                      </p>
                    </div>

                    <OwnerCustomerInsightsChart
                      data={customerData}
                      series={customerSeries}
                      summary={customerSummary}
                      topBySales={customerTopSales}
                      topByOutstanding={customerTopOutstanding}
                      byChannel={customerByChannel}
                      overLimit={customerOverLimit}
                      loading={customerLoading}
                    />
                  </div>
                </div>
                </>
                )}
              </div>
            )}
    </>
  );

  if (embedded) {
    return <div className="admin-embedded-owner-view">{dashboardBody}</div>;
  }

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={32} operation={4} op={3} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title="Owner Dashboard" />
          <div className="container pb-3">{dashboardBody}</div>
        </div>
      </div>
    </div>
  );
};

export default OwnerDashboard;
